import React, { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaShoppingBasket, FaTrash } from "react-icons/fa";

import EmptyState from "../components/EmptyState";
import ProductImage from "../components/ProductImage";
import UpsellModule from "../components/UpsellModule";
import { addToCart, removeFromCart, clearCart } from "../actions/cartActions";
import { createOrder } from "../actions/orderActions";
import { t } from "../i18n/translations";

import "./CartScreen.css";

function CartScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { language } = useSelector((state) => state.language);

  /* ===== QUERY PARAMS ===== */
  const query = new URLSearchParams(location.search);
  const productId = query.get("product");
  const qty = Number(query.get("qty")) || 1;
  const tableFromUrl = query.get("table");

  /* ===== REDUX ===== */
  const { cartItems } = useSelector((state) => state.cart);
  const { order, success } = useSelector((state) => state.orderCreate);
  const { restaurant } = useSelector((state) => state.restaurantDetails || {});
  const tablesEnabled = restaurant?.tables_enabled !== false;
  const hidePrices = restaurant?.hide_prices === true;
  // Same rule Product.js/ProductDetailScreen.js gate quick-add/add-to-cart
  // on — checked again here in case the cart was filled before the
  // restaurant closed. Also enforced server-side (addOrderItems).
  const isClosed = restaurant?.is_open_now === false;

  /* ===== LOCAL STATE ===== */
  const [showEmptyMessage, setShowEmptyMessage] = useState(false);
  const [table, setTable] = useState(tableFromUrl || "");
  const [note, setNote] = useState("");
  const [tableError, setTableError] = useState(false);

  /* ===== ADD TO CART FROM QUERY ===== */
  useEffect(() => {
    if (productId) {
      dispatch(addToCart(productId, qty));
    }

    const timer = setTimeout(() => setShowEmptyMessage(true), 300);
    return () => clearTimeout(timer);
  }, [dispatch, productId, qty]);

  /* ===== TOTALS ===== */
  const totalQty = cartItems.reduce((acc, item) => acc + item.qty, 0);
  const totalPrice = cartItems.reduce(
    (acc, item) => acc + item.price * item.qty,
    0,
  );

  /* ===== ORDER SUCCESS ===== */
  useEffect(() => {
    if (success && order) {
      dispatch(clearCart());
      navigate(
        `/order-success?number=${order.restaurant_order_number}&id=${order.id}&token=${order.tracking_token}`,
      );
      dispatch({ type: "ORDER_CREATE_RESET" });
    }
  }, [success, order, dispatch, navigate]);

  /* ===== PLACE ORDER ===== */
  const placeOrder = () => {
    if (isClosed) return;

    if (tablesEnabled && !table) {
      setTableError(true);
      return;
    }

    dispatch(
      createOrder({
        orderItems: cartItems,
        totalPrice,
        table,
        note,
      }),
    );
  };

  return (
    <div className="home-container">
      <h1 className="cart-title">{t("cartTitle", language)}</h1>

      {cartItems.length === 0 && showEmptyMessage ? (
        <EmptyState
          icon={<FaShoppingBasket />}
          title={t("emptyCartTitle", language)}
          message={t("emptyCartMessage", language)}
          action={
            <Link to="/" className="empty-cart-link">
              {t("backToMenu", language)}
            </Link>
          }
        />
      ) : (
        <>
          <div className="cart-items">
            {cartItems.map((item) => (
              <div key={item.product} className="cart-item">
                <button
                  type="button"
                  className="cart-item-remove"
                  onClick={() => dispatch(removeFromCart(item.product))}
                  aria-label={t("removeFromCart", language)}
                >
                  <FaTrash />
                </button>

                <ProductImage
                  src={item.image}
                  alt={item.name}
                  className="cart-item-image"
                />

                <div className="cart-info">
                  <span className="cart-item-name">{item.name}</span>

                  {/* PLUSZ OPCIÓK */}
                  {item.selectedOptions?.length > 0 && (
                    <div className="cart-item-options">
                      {item.selectedOptions.map((opt, idx) => (
                        <div key={idx} className="cart-item-option">
                          {opt.name}
                          {!hidePrices && opt.extra_price > 0 && (
                            <span className="extra-price">
                              {" "}
                              (+{opt.extra_price} Ft)
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="cart-controls">
                    <div className="qty-box">
                      <button
                        type="button"
                        className="qty-btn"
                        disabled={item.qty === 1}
                        onClick={() =>
                          item.qty > 1 &&
                          dispatch(
                            addToCart({
                              ...item,
                              qty: item.qty - 1,
                            }),
                          )
                        }
                      >
                        –
                      </button>

                      <span className="qty-value">{item.qty}</span>

                      <button
                        type="button"
                        className="qty-btn"
                        onClick={() =>
                          dispatch(
                            addToCart({
                              ...item,
                              qty: item.qty + 1,
                            }),
                          )
                        }
                      >
                        +
                      </button>
                    </div>

                    {!hidePrices && (
                      <span className="cart-item-price">
                        {item.price * item.qty} Ft
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ===== ÖSSZEGZÉS ===== */}
          {!hidePrices && (
            <div className="cart-summary">
              <div className="cart-summary-row">
                <span>{t("productsCount", language)(totalQty)}</span>
                <span>{totalPrice} Ft</span>
              </div>
              <div className="cart-summary-row cart-summary-total">
                <span>{t("total", language)}</span>
                <span>{totalPrice} Ft</span>
              </div>
            </div>
          )}

          {!isClosed && (
            <UpsellModule
              context="cart"
              cartProductIds={cartItems.map((item) => item.product)}
            />
          )}

          {/* ===== ASZTAL VÁLASZTÁS — CSAK HA NINCS URL-BEN ÉS AZ ÉTTEREM KÉRI ===== */}
          {tablesEnabled && !tableFromUrl && (
            <div className="table-picker">
              <label className="field-label">
                {t("chooseTable", language)}
              </label>

              <div
                className={`table-chip-row ${tableError ? "has-error" : ""}`}
              >
                {[...Array(10).keys()].map((x) => {
                  const num = x + 1;
                  return (
                    <button
                      key={num}
                      type="button"
                      className={`table-chip ${table === num ? "active" : ""}`}
                      onClick={() => {
                        setTable(num);
                        setTableError(false);
                      }}
                    >
                      {num}
                    </button>
                  );
                })}
              </div>

              {tableError && (
                <div className="table-error-text">
                  {t("chooseTableError", language)}
                </div>
              )}
            </div>
          )}

          {/* ===== MEGJEGYZÉS ===== */}
          <div className="order-note-field">
            <label className="field-label" htmlFor="orderNote">
              {t("orderNote", language)}
            </label>
            <textarea
              id="orderNote"
              className="order-note-input"
              rows={3}
              maxLength={250}
              placeholder={t("orderNotePlaceholder", language)}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="char-count">
              {t("charactersCount", language)(note.length)}
            </div>
          </div>

          <button
            type="button"
            onClick={placeOrder}
            className="btn-order"
            disabled={isClosed}
          >
            <div className="order-count-circle">{totalQty}</div>
            <div className="order-button-text">
              {t("placeOrder", language)}
            </div>
            {!hidePrices && (
              <div className="order-price">{totalPrice} Ft</div>
            )}
          </button>
        </>
      )}
    </div>
  );
}

export default CartScreen;
